import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import {
  OrganizationCreateDto,
  OrganizationDto,
  OrganizationService,
  OrganizationUpdateDto
} from '../services/organization.service';

@Component({
  selector: 'app-organizations-management',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/" class="home-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Home
          </a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="16" rx="2"/>
                <path d="M3 10h18"/>
                <path d="M8 15h3"/>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Organizations</h1>
          <p class="page-sub">Manage organization records, contacts, and operational status.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          @if (message()) {
            <div class="status-banner">{{ message() }}</div>
          }

          <div class="card">
            <div class="card-hd">
              <span class="card-title">All Organizations</span>
              <div class="card-tools">
                <div class="tool-input-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input class="tool-input" placeholder="Filter organizations…" [(ngModel)]="filterText" />
                </div>

                <select class="tool-select" [(ngModel)]="sortMode">
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="status">Status</option>
                </select>

                <span class="card-count">{{ filteredOrganizations.length }} org{{ filteredOrganizations.length !== 1 ? 's' : '' }}</span>
              </div>
            </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Archived Organizations</span>
              <span class="card-count">{{ archivedOrganizations().length }} archived</span>
            </div>

            <div class="table-wrap">
              <div class="table-head">
                <span>Name</span>
                <span>Billing Contact</span>
                <span>Primary Contact</span>
                <span>Status</span>
                <span>Action</span>
              </div>

              @for (org of archivedOrganizations(); track org.id) {
                <div class="table-row">
                  <div>
                    <div class="cell-main">{{ org.displayName || org.legalName }}</div>
                    <small class="cell-sub">{{ org.legalName }}</small>
                  </div>
                  <div>
                    <div class="cell-main">{{ org.billingContactName || '—' }}</div>
                    <small class="cell-sub">{{ org.billingContactEmail || '—' }}</small>
                  </div>
                  <div>
                    <div class="cell-main">{{ org.contactName || '—' }}</div>
                    <small class="cell-sub">{{ org.contactEmail || '—' }}</small>
                  </div>
                  <span>{{ org.status || 'Archived' }}</span>
                  <div>
                    <button class="btn btn--ghost" (click)="restoreArchived(org)">Restore</button>
                    <button class="btn btn--danger" (click)="hardDeleteArchived(org)">Hard Delete</button>
                  </div>
                </div>
              } @empty {
                <div class="empty">No archived organizations.</div>
              }
            </div>
          </div>

            <div class="table-wrap">
              <div class="table-head">
                <span>Name</span>
                <span>Billing Contact</span>
                <span>Primary Contact</span>
                <span>Status</span>
                <span>Action</span>
              </div>

              @for (org of filteredOrganizations; track org.id) {
                <div class="table-row" [class.row--selected]="selected()?.id === org.id">
                  <div>
                    <div class="cell-main">{{ org.displayName || org.legalName }}</div>
                    <small class="cell-sub">{{ org.legalName }}</small>
                  </div>
                  <div>
                    <div class="cell-main">{{ org.billingContactName || '—' }}</div>
                    <small class="cell-sub">{{ org.billingContactEmail || '—' }}</small>
                  </div>
                  <div>
                    <div class="cell-main">{{ org.contactName || '—' }}</div>
                    <small class="cell-sub">{{ org.contactEmail || '—' }}</small>
                  </div>
                  <span>{{ org.status || '—' }}</span>
                  <div>
                    <button class="btn btn--ghost" (click)="selectOrganization(org)">Edit</button>
                  </div>
                </div>
              } @empty {
                <div class="empty">No organizations found.</div>
              }
            </div>
          </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">{{ editMode() ? 'Edit Organization' : 'Create Organization' }}</span>
            </div>

            <div class="edit-layout">
              <div class="edit-section edit-section--full">
                <h3 class="section-title">Name and Contacts</h3>
                <div class="section-grid section-grid--name-contacts">
                  <div class="field-group">
                    <label class="field-label">LegalName</label>
                    <input class="field-input" [(ngModel)]="draft.legalName" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">DisplayName</label>
                    <input class="field-input" [(ngModel)]="draft.displayName" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ContactName</label>
                    <input class="field-input" [(ngModel)]="draft.contactName" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ContactEmail</label>
                    <input class="field-input" [(ngModel)]="draft.contactEmail" />
                  </div>
                </div>
              </div>

              <div class="edit-section">
                <h3 class="section-title">Billing</h3>
                <div class="section-grid">
                  <div class="field-group">
                    <label class="field-label">BillingBusinessName</label>
                    <input class="field-input" [(ngModel)]="draft.billingBusinessName" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">BillingContactName</label>
                    <input class="field-input" [(ngModel)]="draft.billingContactName" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">BillingContactEmail</label>
                    <input class="field-input" [(ngModel)]="draft.billingContactEmail" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">BillingContactPhone</label>
                    <input class="field-input" [(ngModel)]="draft.billingContactPhone" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">BillingAddress1</label>
                    <input class="field-input" [(ngModel)]="draft.billingAddress1" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">BillingAddress2</label>
                    <input class="field-input" [(ngModel)]="draft.billingAddress2" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">BillingCity</label>
                    <input class="field-input" [(ngModel)]="draft.billingCity" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">BillingState</label>
                    <input class="field-input" [(ngModel)]="draft.billingState" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">BillingCountry</label>
                    <input class="field-input" [(ngModel)]="draft.billingCountry" maxlength="3" (ngModelChange)="onBillingCountryChanged()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">BillingZipCode</label>
                    <input class="field-input" [(ngModel)]="draft.billingZipCode" />
                  </div>
                </div>
              </div>

              <div class="edit-section">
                <div class="section-title-row">
                  <h3 class="section-title">Shipping</h3>
                  <label class="checkbox-field checkbox-field--inline">(Same as Billing <input type="checkbox" [(ngModel)]="shippingSameAsBilling" (ngModelChange)="onShippingSameAsBillingChanged()" />)</label>
                </div>
                <div class="section-grid">
                  <div class="field-group">
                    <label class="field-label">ShippingBusinessName</label>
                    <input class="field-input" [(ngModel)]="draft.shippingBusinessName" (ngModelChange)="onShippingFieldEdited()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ShippingContactName</label>
                    <input class="field-input" [(ngModel)]="draft.shippingContactName" (ngModelChange)="onShippingFieldEdited()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ShippingContactEmail</label>
                    <input class="field-input" [(ngModel)]="draft.shippingContactEmail" (ngModelChange)="onShippingFieldEdited()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ShippingContactPhone</label>
                    <input class="field-input" [(ngModel)]="draft.shippingContactPhone" (ngModelChange)="onShippingFieldEdited()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ShippingAddress1</label>
                    <input class="field-input" [(ngModel)]="draft.shippingAddress1" (ngModelChange)="onShippingFieldEdited()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ShippingAddress2</label>
                    <input class="field-input" [(ngModel)]="draft.shippingAddress2" (ngModelChange)="onShippingFieldEdited()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ShippingCity</label>
                    <input class="field-input" [(ngModel)]="draft.shippingCity" (ngModelChange)="onShippingFieldEdited()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ShippingState</label>
                    <input class="field-input" [(ngModel)]="draft.shippingState" (ngModelChange)="onShippingFieldEdited()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ShippingCountry</label>
                    <input class="field-input" [(ngModel)]="draft.shippingCountry" maxlength="3" (ngModelChange)="onShippingCountryChanged()" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">ShippingZipCode</label>
                    <input class="field-input" [(ngModel)]="draft.shippingZipCode" (ngModelChange)="onShippingFieldEdited()" />
                  </div>
                </div>
              </div>
            </div>

            <div class="action-bar">
              <button class="btn btn--primary" (click)="save()">
                {{ editMode() ? 'Update Organization' : 'Create Organization' }}
              </button>
              @if (editMode()) {
                @if (showEnabledToggle) {
                <button class="btn btn--ghost" (click)="toggleEnabled()">{{ enabledToggleLabel }}</button>
                }
                <button class="btn btn--danger" (click)="remove()">Delete</button>
                @if (showHardDelete) {
                <button class="btn btn--danger btn--right" (click)="hardDelete()">Hard Delete</button>
                }
              }
              <button class="btn btn--ghost" (click)="resetForm()">Clear</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page {
      min-height: 100vh;
    }

    .page-hero {
      background:
        radial-gradient(ellipse at 12% 60%, color-mix(in srgb, #10b981 8%, transparent) 0%, transparent 55%),
        linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
      border-bottom: 1px solid var(--color-border);
      padding: 3rem 1.5rem 3.5rem;
    }

    .page-hero-inner,
    .page-body-inner {
      max-width: 1100px;
      margin: 0 auto;
    }

    .home-btn {
      display: inline-flex;
      align-items: center;
      gap: .35rem;
      color: var(--color-muted);
      text-decoration: none;
      font-size: .9rem;
      margin-bottom: .95rem;
    }

    .hero-top-row {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 0.85rem;
    }

    .hero-icon {
      width: 3.25rem;
      height: 3.25rem;
      border-radius: .95rem;
      display: grid;
      place-items: center;
      margin-bottom: .9rem;
      border: 1px solid color-mix(in srgb, #10b981 45%, #0000);
      color: #6ee7b7;
      background: color-mix(in srgb, #10b981 18%, transparent);
      box-shadow: inset 0 0 0 1px color-mix(in srgb, #10b981 24%, transparent);
    }

    .page-title {
      margin: 0;
      font-size: clamp(1.6rem, 3vw, 2.2rem);
      letter-spacing: -.01em;
    }

    .page-sub {
      margin: .5rem 0 0;
      color: var(--color-muted);
      max-width: 60ch;
    }

    .page-body {
      padding: 1.5rem;
    }

    .status-banner {
      margin-bottom: .9rem;
      border: 1px solid color-mix(in srgb, var(--color-accent-soft) 45%, transparent);
      background: color-mix(in srgb, var(--color-accent-soft) 10%, transparent);
      color: var(--color-text);
      border-radius: .75rem;
      padding: .7rem .9rem;
      font-size: .92rem;
    }

    .card {
      border: 1px solid var(--color-border);
      border-radius: .95rem;
      overflow: hidden;
      margin-bottom: 1rem;
      background: color-mix(in srgb, var(--color-panel) 80%, transparent);
    }

    .card-hd {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid var(--color-border);
      padding: .85rem 1rem;
      gap: .6rem;
      flex-wrap: wrap;
    }

    .card-title {
      font-weight: 600;
    }

    .card-tools {
      display: flex;
      align-items: center;
      gap: .55rem;
      flex-wrap: wrap;
    }

    .tool-input-wrap {
      display: inline-flex;
      align-items: center;
      gap: .35rem;
      border: 1px solid var(--color-border);
      border-radius: .55rem;
      padding: .4rem .55rem;
    }

    .tool-input {
      border: 0;
      background: transparent;
      color: var(--color-text);
      min-width: 210px;
      outline: none;
    }

    .tool-select {
      border: 1px solid var(--color-border);
      border-radius: .55rem;
      padding: .4rem .55rem;
      background: transparent;
      color: var(--color-text);
    }

    .card-count {
      color: var(--color-muted);
      font-size: .9rem;
    }

    .table-wrap {
      display: flex;
      flex-direction: column;
    }

    .table-head,
    .table-row {
      display: grid;
      grid-template-columns: 2fr 1.6fr 1.6fr .8fr .8fr;
      gap: .7rem;
      align-items: center;
      padding: .7rem 1rem;
    }

    .table-head {
      border-bottom: 1px solid var(--color-border);
      color: var(--color-muted);
      font-size: .8rem;
      letter-spacing: .02em;
      text-transform: uppercase;
      font-weight: 600;
    }

    .table-row {
      border-bottom: 1px solid var(--color-border);
    }

    .row--selected {
      background: color-mix(in srgb, var(--color-accent-soft) 12%, transparent);
    }

    .cell-main {
      font-weight: 500;
    }

    .cell-sub {
      display: block;
      color: var(--color-muted);
      font-size: .78rem;
      margin-top: .16rem;
    }

    .empty {
      padding: 1rem;
      color: var(--color-muted);
    }

    .section-title-row {
      display: flex;
      align-items: center;
      gap: .5rem;
      flex-wrap: wrap;
    }

    .edit-layout {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: .85rem;
      padding: 1rem;
      align-items: start;
    }

    .edit-section {
      display: flex;
      flex-direction: column;
      gap: .65rem;
    }

    .edit-section--full {
      grid-column: 1 / -1;
    }

    .section-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: .65rem;
    }

    .section-grid--name-contacts {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .section-title {
      margin: .25rem 0 .2rem;
      font-size: .85rem;
      font-weight: 700;
      letter-spacing: .02em;
      text-transform: uppercase;
      color: var(--color-muted);
    }

    .field-input {
      padding: .58rem .68rem;
      border: 1px solid var(--color-border);
      border-radius: .55rem;
      background: transparent;
      color: var(--color-text);
    }

    .field-group {
      display: flex;
      flex-direction: column;
      gap: .35rem;
    }

    .field-label {
      font-size: .78rem;
      color: var(--color-muted);
      letter-spacing: .01em;
    }

    .checkbox-field {
      display: inline-flex;
      align-items: center;
      gap: .45rem;
      color: var(--color-text);
      font-size: .9rem;
      min-height: 2.2rem;
    }

    .checkbox-field--inline {
      min-height: auto;
      font-size: .78rem;
      color: var(--color-muted);
    }

    .action-bar {
      display: flex;
      gap: .6rem;
      padding: 0 1rem 1rem;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: .4rem;
      border-radius: .6rem;
      border: 1px solid var(--color-border);
      background: transparent;
      color: var(--color-text);
      padding: .5rem .8rem;
      cursor: pointer;
    }

    .btn--primary {
      background: color-mix(in srgb, var(--color-accent-soft) 28%, transparent);
      border-color: color-mix(in srgb, var(--color-accent-soft) 60%, var(--color-border));
      color: #fff;
    }

    .btn--ghost {
      background: transparent;
    }

    .btn--danger {
      border-color: color-mix(in srgb, #ef4444 55%, var(--color-border));
      color: #fca5a5;
    }

    .btn--right {
      margin-left: auto;
    }

    @media (max-width: 980px) {
      .table-head {
        display: none;
      }

      .table-row {
        grid-template-columns: 1fr;
        gap: .45rem;
      }

      .edit-layout {
        grid-template-columns: 1fr;
      }

      .section-grid--name-contacts {
        grid-template-columns: 1fr;
      }

      .action-bar {
        flex-wrap: wrap;
      }
    }
  `]
})
export class OrganizationsManagementComponent implements OnInit {
  private readonly organizationService = inject(OrganizationService);

  readonly organizations = signal<OrganizationDto[]>([]);
  readonly archivedOrganizations = signal<OrganizationDto[]>([]);
  readonly selected = signal<OrganizationDto | null>(null);
  readonly editMode = signal(false);
  readonly message = signal('');

  filterText = '';
  sortMode: 'name-asc' | 'name-desc' | 'status' = 'name-asc';
  shippingSameAsBilling = false;

  draft: OrganizationCreateDto = {
    legalName: '',
    billingBusinessName: '',
    shippingBusinessName: '',
    displayName: '',
    billingContactName: '',
    billingContactEmail: '',
    billingContactPhone: '',
    billingAddress1: '',
    billingCity: '',
    billingState: '',
    billingCountry: '',
    billingZipCode: '',
    contactName: '',
    contactEmail: '',
    shippingAddress1: '',
    shippingContactName: '',
    shippingContactEmail: '',
    shippingContactPhone: '',
    shippingCity: '',
    shippingState: '',
    shippingCountry: '',
    shippingZipCode: ''
  };

  ngOnInit(): void {
    this.loadOrganizations();
  }

  loadOrganizations(): void {
    this.organizationService.getAll().subscribe({
      next: data => {
        this.organizations.set(data);
        if (!this.editMode()) {
          return;
        }

        const selectedId = this.selected()?.id;
        if (!selectedId) {
          return;
        }

        const refreshed = data.find(org => org.id === selectedId);
        if (refreshed) {
          this.selectOrganization(refreshed);
          return;
        }

        this.resetForm();
      },
      error: () => this.message.set('Failed to load organizations (SiteAdmin required).')
    });

    this.organizationService.getArchived().subscribe({
      next: data => this.archivedOrganizations.set(data),
      error: () => this.archivedOrganizations.set([])
    });
  }

  selectOrganization(org: OrganizationDto): void {
    this.selected.set(org);
    this.editMode.set(true);
    this.draft = {
      legalName: org.legalName,
      billingBusinessName: org.billingBusinessName,
      shippingBusinessName: org.shippingBusinessName,
      displayName: org.displayName,
      billingContactName: org.billingContactName,
      billingContactEmail: org.billingContactEmail,
      billingContactPhone: org.billingContactPhone,
      billingAddress1: org.billingAddress1,
      billingAddress2: org.billingAddress2,
      billingCity: org.billingCity,
      billingState: org.billingState,
      billingCountry: org.billingCountry,
      billingZipCode: org.billingZipCode,
      contactName: org.contactName,
      contactEmail: org.contactEmail,
      shippingAddress1: org.shippingAddress1,
      shippingAddress2: org.shippingAddress2,
      shippingContactName: org.shippingContactName,
      shippingContactEmail: org.shippingContactEmail,
      shippingContactPhone: org.shippingContactPhone,
      shippingCity: org.shippingCity,
      shippingState: org.shippingState,
      shippingCountry: org.shippingCountry,
      shippingZipCode: org.shippingZipCode,
      externalKey: org.externalKey,
      status: org.status
    };

    this.shippingSameAsBilling =
      (org.shippingAddress1 || '') === (org.billingAddress1 || '')
      && (org.shippingCity || '') === (org.billingCity || '')
      && (org.shippingState || '') === (org.billingState || '')
      && (org.shippingCountry || '') === (org.billingCountry || '')
      && (org.shippingZipCode || '') === (org.billingZipCode || '');
  }

  onShippingSameAsBillingChanged(): void {
    if (!this.shippingSameAsBilling) {
      return;
    }

    this.copyBillingToShipping();
  }

  onBillingCountryChanged(): void {
    this.draft.billingCountry = (this.draft.billingCountry || '').trim().toUpperCase();
  }

  onShippingCountryChanged(): void {
    this.draft.shippingCountry = (this.draft.shippingCountry || '').trim().toUpperCase();
    this.onShippingFieldEdited();
  }

  onShippingFieldEdited(): void {
    if (!this.shippingSameAsBilling) {
      return;
    }

    this.shippingSameAsBilling = false;
  }

  private copyBillingToShipping(): void {
    this.draft.shippingAddress1 = this.draft.billingAddress1;
    this.draft.shippingAddress2 = this.draft.billingAddress2;
    this.draft.shippingBusinessName = this.draft.billingBusinessName;
    this.draft.shippingContactName = this.draft.billingContactName;
    this.draft.shippingContactEmail = this.draft.billingContactEmail;
    this.draft.shippingContactPhone = this.draft.billingContactPhone;
    this.draft.shippingCity = this.draft.billingCity;
    this.draft.shippingState = this.draft.billingState;
    this.draft.shippingCountry = this.draft.billingCountry;
    this.draft.shippingZipCode = this.draft.billingZipCode;
  }

  toggleEnabled(): void {
    const selected = this.selected();
    if (!selected || selected.isDeleted) {
      return;
    }

    const nextEnabled = !selected.isEnabled;
    this.organizationService.setEnabled(selected.id, { isEnabled: nextEnabled }).subscribe({
      next: () => {
        this.message.set(`Organization ${nextEnabled ? 'enabled' : 'disabled'}.`);
        this.loadOrganizations();
      },
      error: () => this.message.set('Failed to update organization enabled state.')
    });
  }

  get enabledToggleLabel(): string {
    return this.selected()?.isEnabled ? 'Disable' : 'Enable';
  }

  get showEnabledToggle(): boolean {
    const selected = this.selected();
    if (!selected) {
      return false;
    }

    return selected.isDeleted !== true;
  }

  get showHardDelete(): boolean {
    const selected = this.selected();
    if (!selected) {
      return false;
    }

    return selected.isDeleted === true && selected.isEnabled === false;
  }

  save(): void {
    const isUpdate = this.editMode() && !!this.selected();

    if (this.shippingSameAsBilling) {
      this.copyBillingToShipping();
    }

    if (!isUpdate && (!this.draft.legalName || !this.draft.billingBusinessName || !this.draft.shippingBusinessName || !this.draft.billingContactEmail || !this.draft.contactEmail)) {
      this.message.set('Required fields are missing.');
      return;
    }

    if (this.draft.billingCountry && this.draft.billingCountry.trim().length > 3) {
      this.message.set('Billing Country must be 3 letters or fewer.');
      return;
    }

    if (this.draft.shippingCountry && this.draft.shippingCountry.trim().length > 3) {
      this.message.set('Shipping Country must be 3 letters or fewer.');
      return;
    }

    if (isUpdate) {
      const updateDto: OrganizationUpdateDto = { ...this.draft };
      this.organizationService.update(this.selected()!.id, updateDto).subscribe({
        next: () => {
          this.message.set('Organization updated.');
          this.loadOrganizations();
        },
        error: () => this.message.set('Failed to update organization.')
      });
      return;
    }

    this.organizationService.create(this.draft).subscribe({
      next: () => {
        this.message.set('Organization created.');
        this.loadOrganizations();
        this.resetForm();
      },
      error: () => this.message.set('Failed to create organization.')
    });
  }

  remove(): void {
    const selected = this.selected();
    if (!selected) return;

    this.organizationService.delete(selected.id).subscribe({
      next: () => {
        this.message.set('Organization deleted.');
        this.loadOrganizations();
        this.resetForm();
      },
      error: () => this.message.set('Failed to delete organization.')
    });
  }

  hardDelete(): void {
    const selected = this.selected();
    if (!selected || !this.showHardDelete) {
      return;
    }

    const confirmed = window.confirm('Hard delete this organization and permanently remove all related data? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    this.organizationService.hardDelete(selected.id).subscribe({
      next: () => {
        this.message.set('Organization hard deleted.');
        this.loadOrganizations();
        this.resetForm();
      },
      error: () => this.message.set('Failed to hard delete organization. Ensure it is deleted and disabled first.')
    });
  }

  restoreArchived(org: OrganizationDto): void {
    this.organizationService.restore(org.id).subscribe({
      next: () => {
        this.message.set(`Organization ${org.id} restored.`);
        this.loadOrganizations();
      },
      error: () => this.message.set('Failed to restore organization.')
    });
  }

  hardDeleteArchived(org: OrganizationDto): void {
    const confirmed = window.confirm(`Hard delete organization ${org.displayName || org.legalName} (ID: ${org.id}) and all related data? This action cannot be undone.`);
    if (!confirmed) {
      return;
    }

    this.organizationService.hardDelete(org.id).subscribe({
      next: () => {
        this.message.set(`Organization ${org.id} hard deleted.`);
        this.loadOrganizations();

        if (this.selected()?.id === org.id) {
          this.resetForm();
        }
      },
      error: () => this.message.set('Failed to hard delete organization. Ensure it is deleted and disabled first.')
    });
  }

  resetForm(): void {
    this.editMode.set(false);
    this.selected.set(null);
    this.shippingSameAsBilling = false;
    this.draft = {
      legalName: '',
      billingBusinessName: '',
      shippingBusinessName: '',
      displayName: '',
      billingContactName: '',
      billingContactEmail: '',
      billingContactPhone: '',
      billingAddress1: '',
      billingCity: '',
      billingState: '',
      billingCountry: '',
      billingZipCode: '',
      contactName: '',
      contactEmail: '',
      shippingAddress1: '',
      shippingContactName: '',
      shippingContactEmail: '',
      shippingContactPhone: '',
      shippingCity: '',
      shippingState: '',
      shippingCountry: '',
      shippingZipCode: ''
    };
  }

  get filteredOrganizations(): OrganizationDto[] {
    const term = this.filterText.trim().toLowerCase();

    const filtered = this.organizations().filter(org => {
      if (!term) {
        return true;
      }

      const name = (org.displayName || org.legalName || '').toLowerCase();
      const legalName = (org.legalName || '').toLowerCase();
      const billingEmail = (org.billingContactEmail || '').toLowerCase();
      const contactEmail = (org.contactEmail || '').toLowerCase();

      return name.includes(term)
        || legalName.includes(term)
        || billingEmail.includes(term)
        || contactEmail.includes(term);
    });

    return filtered.sort((a, b) => {
      const nameA = (a.displayName || a.legalName || '').toLowerCase();
      const nameB = (b.displayName || b.legalName || '').toLowerCase();

      if (this.sortMode === 'name-desc') {
        return nameB.localeCompare(nameA);
      }

      if (this.sortMode === 'status') {
        return (a.status || '').localeCompare(b.status || '') || nameA.localeCompare(nameB);
      }

      return nameA.localeCompare(nameB);
    });
  }
}
